import PropTypes from 'prop-types';
import React from 'react';
import { Editor, Draft } from '@ublend-npm/th90-editor';
import PLUGINS from '@ublend-npm/th90-plugins';
import * as endpoints from '../../../../constants/endpoints';
import withErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { ERROR_EDITOR } from '../../../../constants/texts';
import { fetchAssignments as fetchAssignmentsAPI } from '../../../../../core/assignments/api';
import getAnalyticsHeaders from '../../../../../core/utils/analyticsHeaders';
import { BIG_BLUE_BUTTON } from '../../../../../core/constants/endpoints';
import goToSubmissions from '../../../../actions/navigation/goToSubmissions.action';

const EditorPlugins = PLUGINS.map((sp) => sp.plugin);

const noop = () => null;

const getExtractedTextFromContent = (content) => {
  const draftContentState = Draft.convertFromRaw(content);
  const extractedContent = `"${draftContentState.getPlainText()}"`;
  return extractedContent;
};

const scraperUrl = () => ({
  scraperUrl: endpoints.WEB_SCAPER(),
});

const linkScraperUrl = (sessionToken) => ({
  scraperUrl: endpoints.AULA_WEB_SCAPER(),
  sessionToken,
});

const EditorReadOnly = ({
  accessToken,
  institution,
  isInstructor,
  spaceId,
  spaceName,
  th90Ref,
  user,
  uniqueId,
  ...props
}) => {
  const scraperObj = scraperUrl();
  const linkScraper = linkScraperUrl(user.sessionToken);
  const { objectId: userId, email, firstName, lastName, sessionToken } = user;
  return (
    <React.Fragment>
      <Editor
        plugins={EditorPlugins}
        th90Ref={th90Ref}
        readOnly
        plugin-config-file={{
          userId,
        }}
        plugin-config-ilos={scraperObj}
        plugin-config-link={linkScraper}
        plugin-config-gform={scraperObj}
        plugin-config-cloudapp={scraperObj}
        plugin-config-gdoc={scraperObj}
        plugin-config-gslides={scraperObj}
        plugin-config-gspreadsheet={scraperObj}
        plugin-config-msform={scraperObj}
        plugin-config-onedrive={scraperObj}
        plugin-config-flatio={scraperObj}
        plugin-config-typeform={scraperObj}
        plugin-config-prezi={scraperObj}
        plugin-config-codepen={scraperObj}
        plugin-config-padlet={scraperObj}
        plugin-config-nearpod={scraperObj}
        plugin-config-clypit={scraperObj}
        plugin-config-oembed={endpoints.OEMBED_PROXY()}
        plugin-config-handin={{ sessionToken, userId }}
        plugin-config-turnitinProvider={{
          resourceEndpoint: endpoints.ASSIGNMENTS(),
          baseUrl: endpoints.LTI_CONSUMER(),
          userId,
          sessionToken,
          email,
          firstName,
          lastName,
          hideButton: !isInstructor,
          role: isInstructor,
        }}
        plugin-config-assignments={{
          user,
          assignmentsEndpoint: endpoints.ASSIGNMENTS(),
          ltiConsumerEndpoint: endpoints.LTI_CONSUMER(),
          fetchAssignments: ({ assignmentId, page, externalId }) =>
            fetchAssignmentsAPI({
              accessToken,
              userId: user.objectId,
              sessionToken: user.sessionToken,
              spaceId,
              assignmentId,
              page,
              externalId,
            }),
          institution,
          isInstructor: (cb) => cb(isInstructor),
          classRoomId: (cb) => cb(spaceId),
          goToAulaSubmissions: (assignmentId, provider) =>
            goToSubmissions({
              spaceId,
              assignmentId,
              provider,
            })(),
        }}
        plugin-config-bbb={{
          ltiConsumerEndpoint: endpoints.BBB_LTI_CONSUMER(),
          userId,
          sessionToken,
          spaceId,
          sessionDescription: `Join the session for the ${spaceName} space`,
          buildUrl: BIG_BLUE_BUTTON,
          analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
        }}
        plugin-config-bibliu={{
          ltiConsumerEndpoint: endpoints.BIBLIU_LTI_CONSUMER(),
          userId,
          sessionToken,
          classRoomId: spaceId,
          analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
        }}
        plugin-config-talis={{
          ltiConsumerEndpoint: endpoints.TALIS_LTI_CONSUMER(),
          userId,
          sessionToken,
          spaceId,
          isInstructor,
          analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
        }}
        plugin-config-echo={{
          ltiConsumerEndpoint: endpoints.ECHO_LTI_CONSUMER(),
          userId,
          sessionToken,
          spaceId,
          isInstructor,
          analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
        }}
        plugin-config-panopto={{
          ltiConsumerEndpoint: endpoints.PANOPTO_LTI_CONSUMER(),
          userId,
          sessionToken,
          spaceId,
          isInstructor,
          analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
        }}
        plugin-config-planet={{
          ltiConsumerEndpoint: endpoints.PLANET_LTI_CONSUMER(),
          userId,
          sessionToken,
          spaceId,
          isInstructor,
          analyticsInfo: getAnalyticsHeaders()['analytics-client-info'],
        }}
        ariaDescribedBy={uniqueId}
        {...props}
      />
      <div style={{ display: 'none' }} id={uniqueId}>
        {getExtractedTextFromContent(props.rawEditorState)}
      </div>
    </React.Fragment>
  );
};

EditorReadOnly.propTypes = {
  accessToken: PropTypes.string.isRequired,
  institution: PropTypes.string.isRequired,
  isInstructor: PropTypes.bool.isRequired,
  scraperUrl: PropTypes.func.isRequired,
  spaceId: PropTypes.string.isRequired,
  spaceName: PropTypes.string.isRequired,
  th90Ref: PropTypes.func,
  uniqueId: PropTypes.string,
  user: PropTypes.shape({
    objectId: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    sessionToken: PropTypes.string.isRequired,
  }).isRequired,
};

EditorReadOnly.defaultProps = {
  th90Ref: noop,
  uniqueId: new Date().getTime(),
};

export default withErrorBoundary(EditorReadOnly, ERROR_EDITOR);
